import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ModalPopup } from "_components";
import { alertActions } from "_store";
import { useDispatch } from "react-redux";
import { Delete, Deletewhite } from "images";
import { ERR_MSG } from "constants/labels";
import PipelineEdit from "../PipelineEdit";
import { PIPELINE_FIELD_MAPPING } from "../../utils";

const fm = PIPELINE_FIELD_MAPPING;

const textFilterFn = (row, columnId, filterValue) =>
  row
    .getValue(columnId)
    ?.toLowerCase()
    .includes(filterValue.toLowerCase());

const PipelineTable = ({
  rawTableData = [],
  handleRefresh,
  updateApi,
  deleteApi,
  header = "Manage Marketer Pipelines",
  emptyMessage = "No pipeline data found.",
  deleteMessage = "Are you sure you want to deactivate the selected pipeline?",
  deleteMessageBulk = "Are you sure you want to deactivate the selected pipelines?",
}) => {
  const dispatch = useDispatch();

  const [editAnchorEl, setEditAnchorEl] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  // Row selection state
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);

  const handleEditClick = (event, pipelineData) => {
    setEditAnchorEl(event.currentTarget);
    setSelectedPipeline(pipelineData);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
    setEditAnchorEl(null);
    setSelectedPipeline(null);
  };

  const handleDeleteClick = (pipelineData) => {
    setSelectedRowForDelete(pipelineData);
    setIsDeleteModalOpen(true);
  };

  const deletePipelines = async (ids) => {
    try {
      dispatch(alertActions.clear());

      const result = await dispatch(
        deleteApi({ [fm.deleteIdsKey]: ids })
      ).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message || ERR_MSG["073"],
            header,
          })
        );
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Deactivated successfully.",
          header,
          showAfterRedirect: true,
        })
      );

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
    } finally {
      setRowSelection({});
      setSelectedRows([]);
    }
  };

  const handleDeletePipeline = async () => {
    if (!selectedRowForDelete) return;
    await deletePipelines([selectedRowForDelete[fm.id]]);
    setIsDeleteModalOpen(false);
    setSelectedRowForDelete(null);
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    await deletePipelines(selectedRows.map((row) => row[fm.id]));
    setIsBulkDeleteModalOpen(false);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: fm.name,
        header: fm.nameLabel,
        size: 200,
        filterFn: textFilterFn,
      },
      {
        accessorKey: fm.description,
        header: fm.descriptionLabel,
        size: 300,
        filterFn: textFilterFn,
      },
      {
        id: "status",
        accessorFn: (row) => (row[fm.isActive] ? "Active" : "Inactive"),
        header: "Status",
        size: 120,
        filterFn: textFilterFn,
      },
      {
        id: "consumptionVisible",
        accessorFn: (row) => (row[fm.isConsumptionVisible] ? "Yes" : "No"),
        header: fm.isConsumptionVisibleLabel,
        size: 160,
        filterFn: textFilterFn,
      },
      {
        id: "storageVisible",
        accessorFn: (row) => (row[fm.isStorageVisible] ? "Yes" : "No"),
        header: fm.isStorageVisibleLabel,
        size: 160,
        filterFn: textFilterFn,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Tooltip title="Edit">
              <IconButton
                color="primary"
                onClick={(e) => handleEditClick(e, row.original)}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deactivate">
              <IconButton
                color="error"
                onClick={() => handleDeleteClick(row.original)}
                size="small"
              >
                <img
                  src={Delete}
                  alt="Deactivate"
                  style={{ width: 20, height: 20 }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const renderTopToolbar = () => (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        padding: "8px",
        flexWrap: "wrap",
      }}
    >
      <Tooltip title="Deactivate Selected" className="DeleteSelected">
        <div>
          <IconButton
            className="delete"
            onClick={() => setIsBulkDeleteModalOpen(true)}
            disabled={selectedRows.length === 0}
          >
            <img src={Deletewhite} alt="Deactivate" />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );

  const table = useMaterialReactTable({
    columns,
    data: rawTableData,
    enableExpanding: false,
    enablePagination: true,
    enableColumnFilters: true,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableFilters: true,
    enableColumnFilterModes: false,
    columnFilterDisplayMode: "popover",
    enableHiding: false,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getRowId: (row) => row[fm.id]?.toString(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection: rowSelection,
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#0F3557",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
      },
    },
    muiTableBodyCellProps: {
      sx: { textAlign: "center" },
    },
    renderTopToolbarCustomActions: renderTopToolbar,
    renderEmptyRowsFallback: () => (
      <Typography sx={{ p: 3, textAlign: "center", fontStyle: "italic" }}>
        {emptyMessage}
      </Typography>
    ),
  });

  // Update selected rows when row selection changes
  useEffect(() => {
    const selectedFlatRows = table.getSelectedRowModel().flatRows;
    setSelectedRows(selectedFlatRows.map((row) => row.original));
  }, [rowSelection]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <MaterialReactTable table={table} />
      </Box>

      {/* Pipeline Edit Component */}
      <PipelineEdit
        pipelineData={selectedPipeline}
        pipelines={rawTableData}
        anchorEl={editAnchorEl}
        isOpen={isEditOpen}
        onClose={handleEditClose}
        handleRefresh={handleRefresh}
        updateApi={updateApi}
        header={header}
      />

      {/* Delete Single Modal */}
      {isDeleteModalOpen && (
        <ModalPopup
          header="Deactivate"
          message1={deleteMessage}
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleDeletePipeline}
          handleSecondaryClick={() => {
            setIsDeleteModalOpen(false);
            setSelectedRowForDelete(null);
          }}
        />
      )}

      {/* Bulk Delete Modal */}
      {isBulkDeleteModalOpen && (
        <ModalPopup
          header="Deactivate Selected"
          message1={selectedRows.length > 1 ? deleteMessageBulk : deleteMessage}
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleBulkDelete}
          handleSecondaryClick={() => {
            setIsBulkDeleteModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default PipelineTable;
